import { useLocation, Link } from 'react-router-dom';
import { Activity, Grid3x3, Gauge, Square, X } from 'lucide-react';
import { useActiveSounds, activeSounds } from '@/lib/activeSounds';
import { MiniDroneBar } from '@/components/MiniDroneBar';

// Each sound is hidden on its own page, where its transport is already visible.
const META = {
  player: { to: '/', Icon: Activity, own: ['/'] },
  sequencer: { to: '/sequencer', Icon: Grid3x3, own: ['/sequencer'] },
  click: { to: '/bordone', Icon: Gauge, own: ['/tuner', '/bordone'] },
};

const SoundChip = ({ sound }) => {
  const { to, Icon } = META[sound.id];
  return (
    <div data-testid={`active-sound-${sound.id}`} className="shrink-0 rounded-full border border-pink-400/40 bg-[#0A0A0A]/95 backdrop-blur-xl shadow-[0_0_20px_rgba(236,72,153,0.18)] pl-1 sm:pl-2 pr-1.5 py-1.5 flex items-center gap-1 sm:gap-2 animate-in fade-in slide-in-from-bottom-2 duration-200">
      <Link to={to} data-testid={`active-sound-open-${sound.id}`} aria-label={sound.label} className="h-8 px-2 rounded-full flex items-center gap-1.5 text-pink-300 hover:bg-white/5">
        <Icon className="w-4 h-4" />
        <span className="hidden sm:inline text-[10px] uppercase tracking-[0.2em] text-white/60">{sound.label}</span>
        {sound.detail && <span className="font-mono text-xs text-white">{sound.detail}</span>}
      </Link>
      <button data-testid={`active-sound-stop-${sound.id}`} aria-label={`Ferma ${sound.label}`} onClick={sound.stop} className="h-8 w-8 rounded-full bg-pink-400 text-black flex items-center justify-center active:scale-95 transition-transform">
        <Square className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};

// Bottom bar with every sound currently playing (any page), each with its own Stop.
export const ActiveSoundsBar = () => {
  const sounds = useActiveSounds();
  const { pathname } = useLocation();
  const chips = sounds.filter((s) => META[s.id] && !META[s.id].own.includes(pathname));
  const showDrone = sounds.some((s) => s.id === 'drone') && pathname !== '/bordone';
  if (!chips.length && !showDrone) return null;
  // Player has a fixed mobile transport bar; sit above it there.
  const offset = pathname === '/' ? 'bottom-[calc(var(--donation-h)+env(safe-area-inset-bottom)+5.5rem)]' : 'bottom-[calc(var(--donation-h)+env(safe-area-inset-bottom)+0.75rem)]';
  return (
    <>
      <div aria-hidden className="h-16" data-testid="active-sounds-spacer" />
      <div data-testid="active-sounds-bar" className={`fixed left-3 right-3 sm:right-auto sm:left-6 z-[55] sm:max-w-[calc(100vw-3rem)] flex flex-nowrap sm:flex-wrap items-center gap-2 overflow-x-auto [scrollbar-width:none] ${offset} sm:bottom-[calc(var(--donation-h)+env(safe-area-inset-bottom)+1rem)]`}>
      {sounds.length > 1 && (
        <button data-testid="active-sounds-stop-all" aria-label="Ferma tutto" onClick={() => activeSounds.stopAll()} className="shrink-0 h-11 px-3 sm:px-4 rounded-full border border-white/15 bg-[#0A0A0A]/95 text-[10px] uppercase tracking-[0.2em] text-white/70 flex items-center gap-1.5 hover:bg-white/5">
          <X className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Ferma tutto</span>
        </button>
      )}
      <MiniDroneBar />
      {chips.map((s) => <SoundChip key={s.id} sound={s} />)}
      </div>
    </>
  );
};
